import { MyPlanLogoDark, MyPlanLogoLight } from "myplan/components/Logo";
import backgroundPattern from "./resources/doodles-bg.png";
import backgroundParallax from "./resources/parallax-background.png";

import { TextStyle } from "react-native";

const headingExtraBold = "PlayfairDisplay_800ExtraBold";
const headingSemiBold = "PlayfairDisplay_600SemiBold";
const headingBoldItalic = "PlayfairDisplay_700Bold_Italic";

const bodyMedium = "MavenPro_500Medium";
const bodyRegular = "MavenPro_400Regular";
const bodyBold = "MavenPro_700Bold";
const bodyItalic = "MavenPro_500Medium";

const monoRegular = "SpaceMono_400Regular";
const monoBoldItalic = "SpaceMono_700Bold_Italic";

const textStyles = {
    h1: {
        fontFamily: headingSemiBold,
        fontSize: 30,
        letterSpacing: 1.2,
        lineHeight: 35,
    },
    h2: {
        fontFamily: headingExtraBold,
        fontSize: 20,
        letterSpacing: 1.2,
        lineHeight: 25,
    },
    h3: {
        fontFamily: headingBoldItalic,
        fontSize: 30,
        letterSpacing: 1.2,
        lineHeight: 35,
    },
    h4: {
        fontFamily: headingSemiBold,
        fontSize: 20,
        lineHeight: 25,
    },
    h5: {
        fontFamily: headingSemiBold,
        fontSize: 30,
        lineHeight: 35,
    },
    h6: {
        fontFamily: headingSemiBold,
        fontSize: 45,
        lineHeight: 45,
        letterSpacing: 2.4,
    },
    h7: {
        fontFamily: headingBoldItalic,
        fontSize: 20,
        letterSpacing: 2.4,
    },
    h8: {
        fontFamily: headingSemiBold,
        fontSize: 25,
        lineHeight: 35,
    },
    h9: {
        fontFamily: bodyMedium,
        fontSize: 15,
        lineHeight: 20,
    },
    h10: {
        fontFamily: bodyRegular,
        fontSize: 20,
        lineHeight: 22,
    },
    body1: {
        fontFamily: bodyRegular,
        fontSize: 20,
        lineHeight: 25,
    },
    body2: {
        fontFamily: bodyRegular,
        fontSize: 15,
        lineHeight: 20,
    },
    body3: {
        fontFamily: bodyMedium,
        fontSize: 12,
        lineHeight: 16,
    },
    body4: {
        fontFamily: bodyRegular,
        fontSize: 12,
        lineHeight: 20,
    },
    body5: {
        fontFamily: bodyRegular,
        fontSize: 15,
        lineHeight: 20,
    },
    mono1: {
        fontFamily: monoBoldItalic,
        fontSize: 20,
        lineHeight: 30,
    },
    mono2: {
        fontFamily: monoBoldItalic,
        fontSize: 20,
        lineHeight: 25,
        letterSpacing: -0.8,
    },
    mono3: {
        fontFamily: monoBoldItalic,
        fontSize: 20,
        lineHeight: 25,
        letterSpacing: -0.4,
    },
    mono4: {
        fontFamily: monoRegular,
        fontSize: 20,
        lineHeight: 25,
        letterSpacing: -0.4,
    },
    cta1: {
        fontFamily: bodyMedium,
        fontSize: 25,
        lineHeight: 30,
    },
    cta2: {
        fontFamily: bodyMedium,
        fontSize: 20,
        lineHeight: 25,
    },
    cta3: {
        fontFamily: bodyMedium,
        fontSize: 20,
        lineHeight: 25,
    },
    ctah2: {
        fontFamily: headingExtraBold,
        fontSize: 20,
        letterSpacing: 1.2,
        lineHeight: 25,
    },
    ctah3: {
        fontFamily: monoBoldItalic,
        fontSize: 20,
        lineHeight: 25,
        letterSpacing: -0.8,
    },
    ctah4: {
        fontFamily: bodyRegular,
        fontSize: 20,
        lineHeight: 22,
    },
    ctah5: {
        fontFamily: headingBoldItalic,
        fontSize: 24,
        letterSpacing: 1.2,
        lineHeight: 32,
    },
    caption1: {
        fontFamily: bodyRegular,
        fontSize: 15,
        lineHeight: 20,
    },
    keypad: {
        fontFamily: bodyMedium,
        fontSize: 30,
        lineHeight: 35,
    },
    alert: {
        fontFamily: bodyMedium,
        fontSize: 15,
        lineHeight: 20,
    },
    default: {
        fontFamily: bodyRegular,
        fontSize: 15,
        lineHeight: 20,
    },
};

// a hex color
export type ThemeColor = string;

export type ThemeTextStyle =
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "h7"
    | "h8"
    | "h9"
    | "h10"
    | "body1"
    | "body2"
    | "body3"
    | "body4"
    | "body5"
    | "mono1"
    | "mono2"
    | "mono3"
    | "mono4"
    | "cta1"
    | "cta2"
    | "cta3"
    | "ctah2"
    | "ctah3"
    | "ctah4"
    | "ctah5"
    | "caption1"
    | "keypad"
    | "alert"
    | "default";

export type ThemeColors = {
    accent: ThemeColor;
    secondaryAccent: ThemeColor;
    background: ThemeColor;
    backgroundAccent: ThemeColor;
    backgroundAccent2: ThemeColor;
    transparentBackground: ThemeColor;
    text: ThemeColor;
    link: ThemeColor;
    ctaText: ThemeColor;
    ctaText2: ThemeColor;
    heading: ThemeColor;
    pinText: ThemeColor;
    inputText: ThemeColor;
    interactive: ThemeColor;
    enabled: ThemeColor;
    active: ThemeColor;
    selected: ThemeColor;
    disabled: ThemeColor;
    disabledText: ThemeColor;
    inactiveMultipleSelectBackground: ThemeColor;
    activeMultipleSelectBackground: ThemeColor;
    interstitialBackground: ThemeColor;
    trackColor: ThemeColor;
    logoPrimary: ThemeColor;
    logoSecondary: ThemeColor;
};

export type ThemeBackground = {
    logoDark?: any; // an imported image resource
    logoLight?: any; // an imported image resource
    logo?: any; // an imported image resource
};

export type ThemeFonts = {
    headingExtraBold: string;
    headingSemiBold: string;
    headingBoldItalic: string;
    bodyBold: string;
    bodyItalic: string;
    bodyMedium: string;
    bodyRegular: string;
    monoRegular: string;
    monoBoldItalic: string;
};

export type AppTheme = {
    name: string;
    color: ThemeColors;
    textStyles: Record<ThemeTextStyle, TextStyle>;
    fonts: ThemeFonts;
    backgroundImage: ThemeBackground;
    backgroundPattern: any; // Imported image resource
    backgroundParallax: any; // Imported image resource
    verticalScale?: number; // number, computed on component mount
};

export const darkTheme: AppTheme = {
    name: "dark",
    color: {
        accent: "#E86451",
        // accent: "#00FF00", // debug
        secondaryAccent: "#D69440",
        // background: "#2D514F",
        background: "#07534F",
        backgroundAccent: "#213B3A",
        backgroundAccent2: "rgba(181,181,181,0.25)",
        transparentBackground: "rgba(45, 81, 79, 0.7)",
        text: "#F7F3ED",
        link: "#F7F3ED",
        ctaText: "#F7F3ED",
        ctaText2: "#F7F3ED",
        heading: "#F7F3ED",
        pinText: "#f2f2f2",
        inputText: "#003251",
        interactive: "#30B7B6",
        enabled: "#E86451",
        // enabled: "#00FF00", //debug
        active: "#F69D90",
        selected: "#F69D90",
        disabled: "#CCC6C5",
        disabledText: "#5B5858",
        inactiveMultipleSelectBackground: "rgba(60, 60, 60, .8)",
        activeMultipleSelectBackground: "rgba(232, 100, 81, .64)",
        // interstitialBackground: "rgba(3, 26, 40, 0.6)",
        interstitialBackground: "rgba(0, 0, 0, 0.2)",
        trackColor: "#213B3A",
        logoPrimary: "#F7F3ED",
        logoSecondary: "#30B7B6",
    },
    textStyles,
    fonts: {
        headingExtraBold,
        headingSemiBold,
        headingBoldItalic,
        bodyBold,
        bodyItalic,
        bodyMedium,
        bodyRegular,
        monoRegular,
        monoBoldItalic,
    },
    backgroundImage: { logoDark: MyPlanLogoDark, logo: MyPlanLogoDark },
    backgroundPattern,
    backgroundParallax,
};

export const lightTheme: AppTheme = {
    name: "light",
    color: {
        accent: "#E86451",
        secondaryAccent: "#D69440",
        background: "#f8f3ed",
        backgroundAccent: "#e9e3dc",
        backgroundAccent2: "rgba(181,181,181,0.25)",
        transparentBackground: "rgba(233, 227, 220, 0.7)",
        text: "#073350",
        link: "#073350",
        ctaText: "#003251",
        ctaText2: "#003251",
        heading: "#073350",
        pinText: "#f2f2f2",
        inputText: "#f2f2f2",
        interactive: "#30B7B6",
        enabled: "#E86451",
        // enabled: "#00FF00", //debug
        active: "#F69D90",
        selected: "#F69D90",
        disabled: "#CCC6C5",
        disabledText: "#5B5858",
        inactiveMultipleSelectBackground: "rgba(201, 193, 185, .8)",
        activeMultipleSelectBackground: "rgba(232, 100, 81, .64)",
        interstitialBackground: "rgba(211, 205, 194, 0.4)",
        trackColor: "#e9e3dc",
        logoPrimary: "#1B2A46",
        logoSecondary: "#30B7B6",
    },
    textStyles,
    fonts: {
        headingExtraBold,
        headingSemiBold,
        headingBoldItalic,
        bodyRegular,
        bodyBold,
        bodyItalic,
        bodyMedium,
        monoRegular,
        monoBoldItalic,
    },
    backgroundImage: { logoLight: MyPlanLogoLight, logo: MyPlanLogoLight },
    backgroundPattern,
    backgroundParallax,
};
